import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { resolveServerTrialDays, stripClientTrialFields } from "@/lib/checkout-trial";
import { type StripeEnv, createStripeClient, getServerStripeEnvironment, getStripeErrorMessage } from "@/lib/stripe.server";

type CheckoutSessionResult = { clientSecret?: string; url?: string; flow?: "checkout" | "upgrade" } | { error: string };
type PortalSessionResult = { url: string } | { error: string };

async function resolveOrCreateCustomer(
  stripe: ReturnType<typeof createStripeClient>,
  options: { email?: string; userId?: string },
): Promise<string> {
  if (options.userId && !/^[a-zA-Z0-9_-]+$/.test(options.userId)) {
    throw new Error("Invalid userId");
  }
  if (options.userId) {
    const found = await stripe.customers.search({
      query: `metadata['userId']:'${options.userId}'`,
      limit: 1,
    });
    if (found.data.length) return found.data[0].id;
  }
  if (options.email) {
    const existing = await stripe.customers.list({ email: options.email, limit: 1 });
    if (existing.data.length) {
      const customer = existing.data[0];
      if (options.userId && customer.metadata?.userId !== options.userId) {
        await stripe.customers.update(customer.id, {
          metadata: { ...customer.metadata, userId: options.userId },
        });
      }
      return customer.id;
    }
  }
  const created = await stripe.customers.create({
    ...(options.email && { email: options.email }),
    ...(options.userId && { metadata: { userId: options.userId } }),
  });
  return created.id;
}

function isAllowedReturnUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname;
    if (host.endsWith(".lovable.app") || host.endsWith(".lovable.dev")) return true;
    if (host.endsWith(".lovableproject.com")) return true;
    if (host === "localhost" || host === "127.0.0.1") return true;
    if (host === "routify-tour.com" || host.endsWith(".routify-tour.com")) return true;
    return false;
  } catch {
    return false;
  }
}

export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: {
    priceId: string;
    customerEmail?: string;
    userId?: string;
    returnUrl: string;
    upgradeReturnUrl?: string;
    cancelUrl?: string;
    environment: StripeEnv;
    uiMode?: "embedded" | "hosted";
    affiliateCode?: string;
    affiliateSlug?: string;
  }) => {
    // O período de teste NUNCA pode vir do browser: qualquer campo de trial
    // enviado pelo cliente é removido aqui e ignorado pelo handler.
    stripClientTrialFields(data as unknown as Record<string, unknown>);
    if (!/^[a-zA-Z0-9_-]+$/.test(data.priceId)) throw new Error("Invalid priceId");
    if (!isAllowedReturnUrl(data.returnUrl)) throw new Error("Invalid returnUrl");
    if (data.upgradeReturnUrl && !isAllowedReturnUrl(data.upgradeReturnUrl)) throw new Error("Invalid upgradeReturnUrl");
    if (data.cancelUrl && !isAllowedReturnUrl(data.cancelUrl)) throw new Error("Invalid cancelUrl");
    if (data.affiliateCode && !/^[A-Z0-9_-]{3,32}$/i.test(data.affiliateCode)) delete data.affiliateCode;
    if (data.affiliateSlug && !/^[A-Z0-9_-]{3,48}$/i.test(data.affiliateSlug)) delete data.affiliateSlug;
    return data;
  })
  .handler(async ({ data, context }): Promise<CheckoutSessionResult> => {
    try {
      const environment = getServerStripeEnvironment(data.environment);
      const stripe = createStripeClient(environment);

      const userId = context.userId;
      const { data: userData } = await context.supabase.auth.getUser();
      const customerEmail = userData.user?.email;

      const prices = await stripe.prices.list({ lookup_keys: [data.priceId] });
      if (!prices.data.length) throw new Error("Price not found");
      const stripePrice = prices.data[0];
      const isRecurring = stripePrice.type === "recurring";

      // Never charge a value different from the one configured/presented.
      {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { normalizePricingMap, pickPricingValue, PRICING_SETTINGS_KEYS } = await import("@/lib/commercial-config");
        const { checkoutPriceMatchesConfig } = await import("@/lib/pricing-sync-policy");
        const { data: settingsRows } = await supabaseAdmin
          .from("app_settings")
          .select("key,value")
          .in("key", [...PRICING_SETTINGS_KEYS, "pricing_sync_lock"]);
        const settings = Object.fromEntries((settingsRows ?? []).map((r: any) => [r.key, r.value]));
        const lock = (settings.pricing_sync_lock ?? {}) as { active?: boolean; reason?: string };
        if (lock.active) {
          console.error("Checkout blocked: pricing sync lock active", lock.reason);
          return { error: "Os pagamentos estão temporariamente indisponíveis enquanto atualizamos os preços. Tenta novamente mais tarde." };
        }
        // Compare against the configuration of THIS environment only.
        const rawConfig = (pickPricingValue(settings, environment) ?? {}) as Record<string, unknown>;
        // Only enforce when the founder panel actually configured this lookup_key;
        // otherwise Stripe stays authoritative.
        const configured = rawConfig[data.priceId]
          ? normalizePricingMap(rawConfig)[data.priceId]
          : undefined;
        const match = checkoutPriceMatchesConfig(data.priceId, configured, {
          unit_amount: stripePrice.unit_amount,
          currency: stripePrice.currency,
          recurring: stripePrice.recurring ? { interval: stripePrice.recurring.interval } : null,
        });
        if (!match.ok) {
          console.error("Checkout blocked: price out of sync", match.reason);
          return { error: "O preço deste plano está a ser atualizado. Tenta novamente dentro de instantes." };
        }
      }


      if (isRecurring) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: subscriptions, error: subscriptionsError } = await supabaseAdmin
          .from("subscriptions")
          .select("stripe_subscription_id,stripe_customer_id,price_id,status,current_period_end")
          .eq("user_id", userId)
          .eq("environment", environment)
          .in("status", ["active", "trialing", "past_due", "canceled"])
          .order("created_at", { ascending: false });
        if (subscriptionsError) throw new Error("Unable to verify current subscription");

        const now = Date.now();
        const existing = (subscriptions ?? []).find((row: any) => {
          const end = row.current_period_end ? new Date(row.current_period_end).getTime() : null;
          return end == null || end > now;
        });
        if (existing?.price_id === data.priceId) {
          throw new Error("Já tens este plano ativo.");
        }
        if (existing?.price_id === "pro_monthly" && data.priceId.startsWith("premium_")) {
          throw new Error("O Routify Pro já inclui todas as vantagens Premium.");
        }
        if (existing && data.priceId === "pro_monthly") {
          if (!data.upgradeReturnUrl) throw new Error("Missing upgradeReturnUrl");
          const current = await stripe.subscriptions.retrieve(existing.stripe_subscription_id);
          const item = current.items.data[0];
          if (!item || current.items.data.length !== 1) {
            throw new Error("Esta subscrição precisa de apoio para mudar de plano.");
          }
          const portal = await stripe.billingPortal.sessions.create({
            customer: existing.stripe_customer_id,
            return_url: data.cancelUrl || data.upgradeReturnUrl,
            flow_data: {
              type: "subscription_update_confirm",
              subscription_update_confirm: {
                subscription: existing.stripe_subscription_id,
                items: [{ id: item.id, price: stripePrice.id, quantity: 1 }],
              },
              after_completion: {
                type: "redirect",
                redirect: { return_url: data.upgradeReturnUrl },
              },
            },
          } as any);
          return { url: portal.url, flow: "upgrade" };
        }
      }

      const customerId = await resolveOrCreateCustomer(stripe, { email: customerEmail, userId });

      const paymentMethodConfiguration = environment === "live"
        ? "pmc_1Tc5RFFsDNqTkxn9babvBJCc"
        : "pmc_1TbNdr7rCtWOu8B1TOhxajhL";

      // Resolve affiliate promotion code (if any) so we can auto-apply it.
      let autoPromoCodeId: string | undefined;
      const affiliateCode = data.affiliateCode?.toUpperCase();
      if (affiliateCode) {
        try {
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          const promoField = environment === "live"
            ? "stripe_promotion_code_id_live"
            : "stripe_promotion_code_id_sandbox";
          const { data: aff } = await supabaseAdmin
            .from("affiliates")
            .select(`id, active, starts_at, ends_at, ${promoField}`)
            .eq("code", affiliateCode)
            .maybeSingle();
          const now = Date.now();
          const valid = aff?.active
            && (!aff.starts_at || new Date(aff.starts_at).getTime() <= now)
            && (!aff.ends_at || new Date(aff.ends_at).getTime() > now);
          if (valid) autoPromoCodeId = (aff as any)[promoField] ?? undefined;
        } catch (e) {
          console.warn("affiliate lookup failed", e);
        }
      }

      // Trial decidido exclusivamente no servidor, a partir do plano.
      const serverTrialDays = resolveServerTrialDays(data.priceId);

      const isHostedCheckout = data.uiMode === "hosted";
      const sessionParams = {
        line_items: [{ price: stripePrice.id, quantity: 1 }],
        mode: isRecurring ? "subscription" : "payment",
        locale: "pt",
        automatic_tax: { enabled: true },
        customer: customerId,
        customer_update: { address: "auto", name: "auto" },
        payment_method_configuration: paymentMethodConfiguration,
        // Let the user paste any valid Stripe promotion code (affiliate or otherwise).
        // If we already auto-applied one, discounts[] overrides allow_promotion_codes.
        ...(autoPromoCodeId
          ? { discounts: [{ promotion_code: autoPromoCodeId }] }
          : { allow_promotion_codes: true }),
        metadata: {
          userId,
          ...(data.priceId.startsWith("credits_pack_") && { credits_pack_lookup_key: data.priceId }),
          ...(affiliateCode && { affiliate_code: affiliateCode }),
          ...(data.affiliateSlug && { affiliate_slug: data.affiliateSlug.toUpperCase() }),
        },
        ...(isRecurring && {
          subscription_data: {
            metadata: {
              userId,
              ...(affiliateCode && { affiliate_code: affiliateCode }),
            },
            ...(serverTrialDays > 0 && { trial_period_days: serverTrialDays }),
          },
        }),
        ...(isHostedCheckout
          ? { success_url: data.returnUrl, cancel_url: data.cancelUrl || data.returnUrl }
          : { ui_mode: "embedded_page", return_url: data.returnUrl }),
      } as any;

      const session = await stripe.checkout.sessions.create(sessionParams);


      return isHostedCheckout ? { url: session.url ?? "", flow: "checkout" } : { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

export const createPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { returnUrl?: string; environment: StripeEnv }) => {
    if (data.returnUrl && !isAllowedReturnUrl(data.returnUrl)) throw new Error("Invalid returnUrl");
    return data;
  })
  .handler(async ({ data, context }): Promise<PortalSessionResult> => {
    const { supabase, userId } = context;

    const { data: sub, error: subError } = await supabase
      .from("subscriptions")
      .select("stripe_customer_id")
      .eq("user_id", userId)
      .eq("environment", getServerStripeEnvironment(data.environment))
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (subError || !sub?.stripe_customer_id) {
      return { error: "No subscription found" };
    }

    try {
      const environment = getServerStripeEnvironment(data.environment);
      const stripe = createStripeClient(environment);
      const portal = await stripe.billingPortal.sessions.create({
        customer: sub.stripe_customer_id,
        ...(data.returnUrl && { return_url: data.returnUrl }),
      });
      return { url: portal.url };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });
